import {
  DiffStates,
  DataQuery,
  ParameterConfig,
  ParameterType,
  Attribute,
  TrainingPhraseConfig,
  AgentInfo,
  OrgInfo,
  DeploymentInfo,
  MessageTemplate,
  AgentContextProps,
  OrgContextProps,
  FrameType,
  ProviderType
} from '@interface/index';

import {
  USER_CACHE_KEY,
  ORG_CACHE_KEY,
  AGENT_CACHE_KEY,
  AGENT_LAN,
  AGENT_ROLE,
  AGENT_DEPLOY_MODE,
  ORG_ROLE,
  API,
  AGENT_MODE,
  FEPage,
  AGENT_PRIVACY
} from '@config/index';

import * as Cookies from 'js-cookie';

import * as _ from 'lodash';
import { ParameterMapStore, PathKeyRegxMap, PathKeys, getFullName, parseQuery } from '@lib/index';
import { getEmptyPage } from './skeleton';

export const attributeToParameter: (
  spm: Attribute,
) => ParameterConfig = (spm: Attribute) => {

  return {
    id: spm.id,
    ownerId: spm.ownerId,
    name: spm.name,
    type: ParameterType.ATTRIBUTE,
    promptQuestions: spm.promptQuestions,
    parentId: spm.parentId,
    proto: spm.proto,
    attributes: spm.attributes,
    triggers: spm.triggers,
    multiValue: spm.multiValue,
  };
};

export const dataQueryToParameter: (dq: DataQuery) => ParameterConfig = (
  dq: DataQuery
) => {

  return {
    id: dq.id || '',
    name: dq.name || '',
    type: ParameterType.CLOSURE || '',
    multiValue: dq.multiValue,
    isStatic: dq.functionConfig && dq.functionConfig.isStatic,
    proto: dq.frame && {
      id: dq.frame.id,
      name: dq.frame.name || '',
      type: dq.frame.type as any
    },
    parameters: dq.parameters
  };
};


export function getUserId() {
  return JSON.parse(window.localStorage.getItem(USER_CACHE_KEY) || '{}').user_id ||
    '';
}

export function getOrgId() {
  const org = getOrgInfo();
  return org && org.id;
}

export function getOrgName() {
  const org = getOrgInfo();
  return org && org.name;
}

export function orgNameToPathComponent(orgName: string) {
  return orgName;
}

export function getOrgNameFromUrl() {
  const path = window.location.pathname;
  const orgName = parseOrgNameFromPath(path);

  if (orgName) {
    return orgName;
  }

  return undefined;
}

export function isTemplateOrg() {
  const org: OrgInfo = JSON.parse(window.sessionStorage.getItem(ORG_CACHE_KEY) || '{}');
  return org.isTemplate;
}

export function getOrgInfo() {
  const cache = window.sessionStorage.getItem(ORG_CACHE_KEY);
  const org: OrgInfo = cache && cache !== 'undefined' && JSON.parse(cache) || undefined;

  if (!org || !org.id) {
    return undefined;
  }

  const orgName = getOrgNameFromUrl();

  if (!orgName) {
    return undefined;
  }

  if (org && orgName !== org.name) {
    return undefined;
  }

  return org;
}

export function isOrgOwner() {
  const org: OrgInfo = JSON.parse(window.sessionStorage.getItem(ORG_CACHE_KEY) || '{}');
  return org.role === ORG_ROLE.OWNER;
}

export function isOrgMember() {
  const org: OrgInfo = JSON.parse(window.sessionStorage.getItem(ORG_CACHE_KEY) || '{}');
  return org.role === ORG_ROLE.OWNER || org.role === ORG_ROLE.MEMBER;
}

export function getUserInfo() {
  return JSON.parse(window.localStorage.getItem(USER_CACHE_KEY) || '{}');
}

export function getAgentInfo() {
  const masterAgent = getMasterAgentInfo();
  if (!masterAgent || !masterAgent.id) {
    return undefined;
  }

  const cache = window.sessionStorage.getItem(AGENT_CACHE_KEY);

  const agent: AgentInfo = cache && cache !== 'undefined' && JSON.parse(cache);
  if (!agent || !agent.id) {
    return undefined;
  }

  if (masterAgent.id !== agent.id) {
    return undefined;
  }

  return agent;
}

export function setMasterAgentInfo(agent?: AgentInfo) {
  if (!agent) {
    window.sessionStorage.removeItem(AGENT_MASTER_CACHE_KEY);
    return;
  }
  window.sessionStorage.setItem(AGENT_MASTER_CACHE_KEY, JSON.stringify(agent));
}

const AGENT_MASTER_CACHE_KEY = 'AGENT_MASTER_CACHE_KEY';

export function getMasterAgentInfo() {

  const name = getAgentNameFromUrl();
  const lang = getAgentLangFromUrl();

  const cache: any = window.sessionStorage.getItem('AGENT_MASTER_CACHE_KEY');
  const agent: AgentInfo = cache && cache.length && cache !== 'undefined' && JSON.parse(cache || '{}');

  if (!agent) {
    return undefined;
  }

  if (agent.name !== name || agent.language !== lang) {
    return undefined;
  }

  return agent;
}

export function getMasterAgentName() {
  const agent: AgentInfo | undefined = getMasterAgentInfo();
  return agent && agent.name;
}

export function getAgentId() {
  const agent = getAgentInfo();
  return agent && agent.id;
}

export function getStructAgentId() {
  const agent: AgentInfo | undefined = getAgentInfo();

  if (!agent) {
    return undefined;
  }

  if (agent.structKey) {
    return agent.structKey.agentId;
  }

  return agent.id;
}

export function getAgentName() {

  const agent = getAgentInfo();
  return agent && agent.name;
}

export function getAgentLang() {

  const agent = getAgentInfo();
  return agent && agent.language;
}

export function getAgentNameFromUrl() {
  const path = window.location.pathname;
  const agentName = parseAgentNameFromPath(path);

  if (agentName) {
    return agentName;
  }

  return undefined;
}

export function getAgentLangFromUrl() {
  const path = window.location.pathname;
  const agentLang = parseAgentLangFromPath(path);

  if (agentLang) {
    return agentLang;
  }

  return undefined;
}

export function getLayerIdFromUrl() {
  const path = window.location.pathname;
  return parseLayerIdFromPath(path);
}

export function getLayerTypeFromUrl() {
  const path = window.location.pathname;
  return parseLayerTypeFromPath(path);
}

export function isBotAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.mode === AGENT_MODE.APPLICATION;
}

export function isOpencuiHostBotAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.mode === AGENT_MODE.APPLICATION && agent.deployMode === AGENT_DEPLOY_MODE.FRAMELY;
}

export function isLibAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.mode === AGENT_MODE.LIBRARY;
}

export function isModuleAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.mode === AGENT_MODE.LIBRARY && !isProviderAgent();
}

export function isServiceAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService;
}

export function isKnowledgeAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.mode === AGENT_MODE.KNOWLEDGE;
}

export function isProviderAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService && !agent.isInterface;
}

export function isHostedProviderAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService && !agent.isInterface && agent.backendProviderType !== ProviderType.Native;
}


export function isNativepProviderAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService && !agent.isInterface && agent.backendProviderType === ProviderType.Native;
}

export function isPublicAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.privacy === AGENT_PRIVACY.PUBLIC;
}

export function isPostgresProviderAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService && !agent.isInterface && agent.backendProviderType === ProviderType.Postgrest;
}

export function isSupabaseProviderAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isService && !agent.isInterface && agent.backendProviderType === ProviderType.Supabase;
}

export function isModuleImplementationAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.isServiceImplementation;
}

export function isStructAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.language === AGENT_LAN.STRUCT;
}

export function isLanguageAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return !!agent.structKey;
}

export function isSingleLanguageAgent() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');

  return agent && agent.id && !agent.structKey && agent.language !== AGENT_LAN.STRUCT;
}

export function getOrgPathPrefix() {
  const path = window.location.pathname;
  const regex = /\/org\/[0-9a-zA-Z_.]+/;
  const matches = path.match(regex);

  if (matches && matches.length) {
    return matches[0];
  }

  return '';
}

export function getAgentPathPrefix() {
  const path = window.location.pathname;
  const regex = /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(zh|en|struct)/;
  const matches = path.match(regex);

  if (matches && matches.length) {
    return matches[0];
  }

  return '';
}

export function getStructPathPrefix() {
  const path = window.location.pathname;
  const regex = /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+/;
  const matches = path.match(regex);

  if (matches && matches.length) {
    return matches[0] + 'struct';
  }

  return '';
}

enum _PayloadType {
  Full = 'full',
  delta = 'delta'
}

export function getCtxFromLocation(location: any) {

  const path = location.pathname || location.path;
  const components = path.split('/');

  const ctx = new Map<string, any>();

  for (let i = 0; i < components.length; i += 1) {
    const comp = components[i];
    if (comp === 'org' && i + 1 < components.length) {
      ctx.set('orgLabel', components[i + 1]);
    } else if (ctx.get('orgLabel') && comp === 'agent' && i + 1 < components.length) {
      ctx.set('agentLabel', components[i + 1]);
      const agent = getAgentInfo();

      if (agent) {
        if (agent.mode === AGENT_MODE.APPLICATION) {
          ctx.set('agentType', 'chatbot');
        } else if (agent.isService && !agent.isInterface) {
          ctx.set('agentType', 'provider');
        } else {
          ctx.set('agentType', 'module');
        }
      }

      if (i + 2 < components.length) {
        ctx.set('lang', components[i + 2]);
      }

    } else if (ctx.get('agentLabel') && ['intent', 'frame', 'entity', 'service'].findIndex((item: string) => item === comp) !== -1) {
      switch (comp) {
        case 'intent':
          ctx.set('type', 'skill');
          break;
        case 'frame':
          ctx.set('type', 'frame');
          break;
        case 'entity':
          ctx.set('type', 'entity');
          break;
        case 'service_schema':
          ctx.set('type', 'service_schema');
          break;
        default:
          break;
      }

      // if (ctx.get('type') && i + 1 < components.length) {
      const typeId = components[i + 1];
      const param = ParameterMapStore.get(typeId);

      if (param && param.name) {
        ctx.set('typeLabel', param.name);
        // ctx.set('slots', {
        //   payload_type: _PayloadType.Full,
        //   payload: param.attributes && param.attributes.filter((item: ParameterConfig) => item.type === ParameterType.ATTRIBUTE)
        // });
      }
      // }
    } else if (ctx.get('typeLabel') && comp === 'slot' && i + 1 < components.length) {
      const slotId = components[i + 1];
      const param = ParameterMapStore.get(slotId);

      if (param && param.name) {
        ctx.set('slotLabel', param.name);

        // if (param.proto && param.proto.name) {
        //   ctx.set('slot_type', param.proto.name);
        // }
      }
    }
  }

  ctx.set('page', getCurrentPage(location));

  const query = parseQuery(location.search);
  if (query && query.tab) {
    ctx.set('tab', query.tab);
  }

  return ctx;
}

export const FEPageKeyRegxMap = {
  [FEPage.orgList]: [/\/org/, /\/org\/add/],
  [FEPage.user]: /\/account/,
  [FEPage.agentList]: /\/org\/[0-9a-zA-Z_.]+\/agent/,
  [FEPage.orgMember]: /\/org\/[0-9a-zA-Z_.]+\/member/,
  [FEPage.orgSetting]: /\/org\/[0-9a-zA-Z_.]+/,
  [FEPage.serviceSchema]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/service_schema/,
  [FEPage.typeList]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/type/,
  [FEPage.skillDetail]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/intent\/\w{24,}/,
  [FEPage.responseDetail]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/intent\/\w{24,}\/response\/\w{24,}/,

  [FEPage.dependency]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/library/,
  [FEPage.frameDetail]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/frame\/\w{24,}/,
  [FEPage.dialogActDetail]: /\/ org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/dialog_act\/\w{24,}/,
  [FEPage.entityDetail]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/entity\/\w{24,}/,
  [FEPage.slotDetail]: [/\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/intent\/\w{24,}\/slot\/\w{24,}/,
    /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/dialog_act\/\w{24,}\/slot\/\w{24,}/,
    /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/frame\/\w{24,}\/slot\/\w{24,}/,
    /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/service\/\w{24,}\/slot\/\w{24,}/,
    /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/service_schema\/slot\/\w{24,}/,
  ],
  [FEPage.agentSetting]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/setting/,
  [FEPage.testCase]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/test_case/,
  [FEPage.version]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/version/,
  [FEPage.providerConnection]: /\/org\/[0-9a-zA-Z_.]+\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/provider_connection/,
};

function getCurrentPage(location: any): FEPage {
  const path = location.pathname || location.path;

  for (const [k, v] of Object.entries(FEPageKeyRegxMap)) {
    if (Array.isArray(v)) {
      for (const regex of v) {
        const ret = path.match(regex);
        if (ret && ret.length && ret[0] === path) {
          return k as FEPage;
        }
      }
    } else {
      const ret = path.match(v);
      if (ret && ret.length && ret[0] === path) {
        return k as FEPage;
      }
    }
  }

  return FEPage.unknown;
}

export function getDuhints() {
  const params = Object.values(ParameterMapStore.all());

  const duHints: any = {
    kind: 'session',
    entities: {
      'io.opencui.core.FrameType': {},
      'io.opencui.core.EntityType': {},
      'io.opencui.core.SlotEntity': {}
    }
  };

  for (const p of Object.values(params)) {

    switch (p.type) {
      case ParameterType.INTENT:
      case ParameterType.COMPOSITE:
        if (p.name && (p.name.startsWith('io.opencui.core') || p.name.startsWith('kotlin.') || p.name.startsWith('java.'))) {
          continue;
        }
        duHints.entities['io.opencui.core.FrameType'][p.name || ''] = p.triggers || [];
        break;
      case ParameterType.ENTITY:
        if (p.name && (p.name.startsWith('io.opencui.core') || p.name.startsWith('kotlin.') || p.name.startsWith('java.'))) {
          continue;
        }
        duHints.entities['io.opencui.core.EntityType'][p.name || ''] = p.triggers || [];
        break;
      case ParameterType.ATTRIBUTE:
        const owner = p.ownerId && ParameterMapStore.get(p.ownerId);
        if (owner && owner.name && (owner.name.startsWith('io.opencui.core') || owner.name.startsWith('kotlin.') || owner.name.startsWith('java.'))) {
          continue;
        }
        duHints.entities['io.opencui.core.SlotEntity'][`${owner && owner.name}/${p.name || ''}`] = p.triggers || [];
        break;
      default:
        break;
    }
  }

  return duHints;
}

export function isReadMode() {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');
  return agent.role === AGENT_ROLE.READ;
}

export function parseOrgIdFromPath(path: string) {
  const result = path.match(/\/org\/([0-9a-zA-Z_.]+)/);
  return result &&
    result.length >= 2 &&
    result[1];
}

export function parseOrgNameFromPath(path: string) {
  const result = path.match(/\/org\/([0-9a-zA-Z_.]+)/);
  const name: string = result &&
    result.length >= 2 &&
    result[1] || '';

  return name;
}

export function parseAgentIdFromPath(path: string) {
  const result = path.match(/\/(agent|service_agent)\/([0-9a-zA-Z_.]+)/);
  return result &&
    result.length >= 3 &&
    result[2];
}

export function parseAgentNameFromPath(path: string) {
  const result = path.match(/\/(agent|service_agent)\/([0-9a-zA-Z_.]+)/);
  return result &&
    result.length >= 3 &&
    result[2] || '';
}

export function parseAgentLangFromPath(path: string) {
  const result = path.match(/\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)/);
  return result &&
    result.length >= 3 &&
    result[2] || '';
}

export function parseLayerIdFromPath(path: string) {

  if (path.match(/\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/service_schema/)) {
    return '';
  }

  const result = path.match(/\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/(intent|frame|entity|service)\/([0-9a-zA-Z_.]+)/);

  return result &&
    result.length >= 5 &&
    result[4] || '';
}

export function parseLayerTypeFromPath(path: string) {
  if (path.match(/\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/service_schema/)) {
    return FrameType.service;
  }
  const result = path.match(/\/(agent|service_agent)\/[0-9a-zA-Z_.]+\/(struct|en|zh)\/(intent|frame|entity|service)\/([0-9a-zA-Z_.]+)/);
  const tp = result &&
    result.length >= 5 &&
    result[3] || '';

  switch (tp) {
    case 'intent':
      return FrameType.intent;
    case 'frame':
      return FrameType.composite;
    case 'entity':
      return FrameType.entity;
    case 'service':
      return FrameType.service;
    default:
      return '';
  }
}

export function getCallbackUrl(integrationType?: string, deployInfo?: DeploymentInfo, label?: string, defaultLang?: string) {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');

  if (agent.deployMode !== AGENT_DEPLOY_MODE.PRIVATE && (!deployInfo || !deployInfo.domain || !deployInfo.domain.length)) {
    return 'Unknown URL,Please deploy first';
  }

  const host = deployInfo && deployInfo.domain;
  const org: OrgInfo = JSON.parse(window.sessionStorage.getItem(ORG_CACHE_KEY) || '{}');
  const orgName = org.name;
  const agentName = agent.name;
  const integrationTypeComp = integrationType && integrationType.length ? `/ ${integrationType} ` : '';
  const labelComp = label && label.length > 0 ? `/ ${label} ` : '';
  const langComp = defaultLang && defaultLang.length > 0 ? `/ ${defaultLang} ` : '';

  let ret = `${agent.deployMode === AGENT_DEPLOY_MODE.PRIVATE
    ? 'https://{host}:{port}' :
    host
    } /org/${orgNameToPathComponent(orgName)} /bot/${agentName} /c${integrationTypeComp}/v1${labelComp}${langComp} `;

  if (!ret.startsWith('https://')) {
    ret = `https://${ret}`;
  }

  return ret;
}

export function getSupportCallbackURL(deployInfo?: DeploymentInfo, label?: string) {
  const agent: AgentInfo = JSON.parse(window.sessionStorage.getItem(AGENT_CACHE_KEY) || '{}');

  if (agent.deployMode !== AGENT_DEPLOY_MODE.PRIVATE && (!deployInfo || !deployInfo.domain || !deployInfo.domain.length)) {
    return 'Unknown URL,Please deploy first';
  }

  const host = deployInfo && deployInfo.domain;
  const org: OrgInfo = JSON.parse(window.sessionStorage.getItem(ORG_CACHE_KEY) || '{}');
  const orgName = org.name;
  const agentName = agent.name;
  const lang = agent.language;
  const labelComp = label ? `/${label}` : '';
  let ret = `${agent.deployMode === AGENT_DEPLOY_MODE.PRIVATE
    ? 'https://{host}:{port}' : host}/org/${orgNameToPathComponent(orgName)}/bot/${agentName}/s/chatwoot/v1${labelComp}`;

  if (!ret.startsWith('https://')) {
    ret = `https://${ret}`;
  }

  return ret;
}

export function renderTime(date: string) {
  if (!date || !date.length) {
    return '';
  }
  const formatDate = new Date(date).toJSON();

  const res = new Date(+new Date(formatDate) + 8 * 3600 * 1000);
  const hour =
    res.getUTCHours() < 10 ? `0${res.getUTCHours()}` : `${res.getUTCHours()}`;
  const minute =
    res.getUTCMinutes() < 10
      ? `0${res.getUTCMinutes()}`
      : `${res.getUTCMinutes()}`;
  const second =
    res.getUTCSeconds() < 10
      ? `0${res.getUTCSeconds()}`
      : `${res.getUTCSeconds()}`;

  return `${res.getUTCFullYear()}-${res.getUTCMonth() +
    1}-${res.getUTCDate()} ${hour}:${minute}:${second}`;
}

export function renderDate(timeStamp: number): string {
  const date = new Date(timeStamp * 1000);

  const year = date.getFullYear();
  // 注意取得的月份为0-11，所以要加1
  const month = date.getMonth() + 1;
  // 注意天的取得，是getDate()，而不是getDay()
  const day = date.getDate();

  // 格式拼接：yyyy-MM-dd HH:mm:ss
  return `${year}-${month}-${day}`;
}

let messageTemplateDic: { [key: string]: MessageTemplate[] };

export async function getMessageTemplates() {
  if (!!messageTemplateDic) {
    return messageTemplateDic;
  }

  try {
    messageTemplateDic = await API.messageTemplate.getAll() as any;
    return messageTemplateDic;
  } catch (e) {
    return {};
  }
}

let messageTemplateMap: { [key: string]: MessageTemplate };

export function getMessageTemplatesSync() {


  if (!!messageTemplateMap) {
    return messageTemplateMap;
  }

  const ret: { [key: string]: MessageTemplate } = {};

  if (!messageTemplateDic) {
    return ret;
  }

  const list = _.flattenDeep(Object.values(messageTemplateDic));

  for (const item of list) {
    ret[item.id || ''] = item;
  }

  messageTemplateMap = ret;

  return ret;
}

export function removeTokenCookie() {
  if (process.env.NODE_ENV === 'development') {
    Cookies.remove('token');
    Cookies.remove('doc');
  } else {

    for (const domain of [
      '.framely.naturali.io',
      '.naturali.io',
      '.build.framely.ai',
      '.build.opencui.io']) {
      Cookies.remove('token', {
        domain,
        path: '/'
      });
    }
  }
}

const LANGUAGE_OPTIONS_CACHE_KEY = 'LANGUAGE_OPTIONS_CACHE_KEY';

export function setCurrentLanguageOptions(opts: any) {
  window.sessionStorage.setItem(LANGUAGE_OPTIONS_CACHE_KEY, JSON.stringify(opts));
}

export function getCurrentLanguageOptions(includingStruct?: boolean) {

  const cache = window.sessionStorage.getItem(LANGUAGE_OPTIONS_CACHE_KEY);

  if (!cache || !cache.length || cache === 'undefined') {
    return undefined;
  }

  const ret = cache && JSON.parse(cache);

  if (!includingStruct) {
    return ret && ret.filter((item: any) => item.label.toLowerCase() !== AGENT_LAN.STRUCT);
  }

  return ret;
}

export function getAgentLangFromLangLabel(label: string): string {

  switch (label) {
    case 'Structure':
      return 'struct';
    default:
      if (label.endsWith('en')) {
        return 'en';
      }
      if (label.endsWith('zh')) {
        return 'zh';
      }
      return label.toLowerCase();
  }
}

export function getRawCodeTypeName(frame: any) {

  if (!frame) {
    return '';
  }

  if (typeof frame === 'string') {
    return expandTypeName(frame);
  }

  const org = getOrgInfo();
  const agent = getAgentInfo();

  return org && agent && (frame.originKey
    ? expandTypeName(getFullName(frame))
    : expandTypeName(`${org.name}.${agent.name}.${getFullName(frame)}`));
}

export function expandTypeName(input: string): string {
  const match = input.match(/\(([^)]+)\)/); // Regex to find content inside parentheses

  if (match) {
    const contentInsideParentheses = match[1];
    const partBeforeParentheses = input.substring(0, match.index).replace(/\.$/, ''); // Extract part before parentheses and remove the trailing dot if one is there
    return `${contentInsideParentheses}.${partBeforeParentheses}`;
  }
  return input;

}

export function orgBaseComp($org: OrgContextProps, comp: JSX.Element) {
  return $org.org && $org.org.id === getOrgId() ? comp : '';
}

export function agentBaseComp($agent: AgentContextProps, comp: JSX.Element) {
  return $agent.agent && $agent.agent.id === getAgentId() ? comp : getEmptyPage();
}

export function packageName(source?: string) {
  if (source && source.length) {
    return `${source}`;
  }

  return `${getOrgName()}.${getAgentName()}`;
}

export function getActualTypeName(name: string, qualification?: string, source?: string) {

  let packageName = '';
  if (source && source.length) {
    packageName = `${source}`;
  } else {
    packageName = `${getOrgName()}.${getAgentName()}`;

  }

  return `${packageName}.${qualification}${(qualification && qualification.length) ? '.' : ''}${name}`;
}

export function parseSource(source?: string) {
  if (!source || !source.length) {
    return {
      orgName: '',
      agentName: ''
    };
  }

  return {
    orgName: source.split('.').slice(0, -1).join('.'),
    agentName: source.split('.').pop() || ''
  };
}

export function isBuildPage() {
  return !isOperatePage() && !isDebugPage();
}

export function isOperatePage() {
  const pathName = window.location.pathname;
  return pathName.endsWith(`${getAgentPathPrefix()}/deploy`)
    || pathName.endsWith(`${getAgentPathPrefix()}/operate_setting`)
    || pathName.endsWith(`${getAgentPathPrefix()}/outbound`);
}

export function isDebugPage() {
  const pathName = window.location.pathname;
  return pathName.endsWith(`${getAgentPathPrefix()}/debug`)
    || pathName.endsWith(`${getAgentPathPrefix()}/debug_setting`);
}

export function isCopilotPage() {
  const pathName = window.location.pathname;
  return pathName.endsWith('copilot');
}

export function getPageTypeName(type: string) {
  switch (type) {
    case 'intent':
      return 'Skill';
    case 'composite':
      return 'Frame';
    case 'entity':
      return 'Entity';
    case 'service':
      return 'Service';
    default:
      return '';
  }
}
